'use memo';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useRef } from 'react';
import { z } from 'zod';

import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Confetti, type ConfettiRef } from '@/components/ui/confetti';
import { useAuthStore } from '@/lib/auth';
import { defaultNS, useTranslation } from '@/lib/i18n';

const CompletedSchema = z.object({
  addedContractId: z.string(),
});

export const Route = createFileRoute(
  '/_staff/contract/add/subscription/completed',
)({
  validateSearch: zodValidator(CompletedSchema),
  loaderDeps: ({ search }) => ({ search }),
  component: () => <Completed />,
});

function Completed() {
  const confettiRef = useRef<ConfettiRef>(null);
  const { session } = useAuthStore();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.contract.add.subscription.completed',
  });
  const { search } = Route.useLoaderDeps();
  const navigate = useNavigate();

  return (
    <VStack className="items-center justify-center">
      <Confetti ref={confettiRef} />
      <AttributeCard
        title={t('result')}
        className="w-1/2 p-4"
        actionButton={
          <ActionButton
            variant="secondary"
            onClick={() => {
              navigate({
                to: '/contract/$id',
                params: { id: search.addedContractId },
              });
            }}
          >
            {t('goToContract')}
          </ActionButton>
        }
      >
        <VStack>
          <Text variant="h3">{t('message')}</Text>
          <Spacer />
          <HStack className="items-center">
            <Icon iconName="SquareCheckBig" text={t('completedBy')} />
            <Spacer />
            <Avatar className="grid size-8 place-items-center">
              <AvatarImage src={session?.person?.avatarUrl} />
              <AvatarFallback>
                <Icon iconName="UserRoundIcon" />
              </AvatarFallback>
            </Avatar>
            <Spacer />
            <Text variant="p">{session?.person?.name}</Text>
          </HStack>
        </VStack>
      </AttributeCard>
    </VStack>
  );
}
